import React, { useEffect, useState, useCallback, useContext } from 'react';
import {
  Table, Button, Modal, Form, Select, DatePicker, InputNumber, Segmented,
  Space, Tag, Popconfirm, Typography, message, Row, Col, Tooltip, Popover,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import EmployeeHistoryFields, {
  STATUSES, STATUS_COLOR, historyFormValues, historyPayload,
} from '../components/EmployeeHistoryFields';

const { Title } = Typography;

export default function EmployeeHistoryPage() {
  const currentUser = useContext(UserContext);
  const canEdit     = (currentUser?.CanEditStaffHistory ?? 0) >= 2;

  const [view,    setView]    = useState('history');

  const [users,   setUsers]   = useState([]);
  const [teams,   setTeams]   = useState([]);
  const [jobs,    setJobs]    = useState([]);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterEmployee,  setFilterEmployee]  = useState(null);
  const [filterTeam,      setFilterTeam]      = useState(null);
  const [filterJob,       setFilterJob]       = useState(null);
  const [filterStatus,    setFilterStatus]    = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate,   setFilterEndDate]   = useState(null);
  const [filterGradDate,  setFilterGradDate]  = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form] = Form.useForm();

  // Recruitment Targets
  const [targets,       setTargets]       = useState([]);
  const [loadingTgts,   setLoadingTgts]   = useState(false);
  const [tgtModalOpen,  setTgtModalOpen]  = useState(false);
  const [editingTgt,    setEditingTgt]    = useState(null);
  const [savingTgt,     setSavingTgt]     = useState(false);
  const [tgtForm] = Form.useForm();

  useEffect(() => {
    api.get('/api/employee-history/users').then((r) => setUsers(r.data)).catch(() => {});
    api.get('/api/teams?my_teams=1&no_client_users=1').then((r) => setTeams(r.data)).catch(() => {});
    api.get('/api/jobs').then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/employee-history')
      .then((r) => setRows(r.data))
      .catch(() => message.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, []);

  const loadTargets = useCallback(() => {
    setLoadingTgts(true);
    api.get('/api/recruitment-targets')
      .then((r) => setTargets(r.data))
      .catch(() => message.error('Failed to load recruitment targets'))
      .finally(() => setLoadingTgts(false));
  }, []);

  useEffect(() => { load(); loadTargets(); }, [load, loadTargets]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ ScheduledHours: 40 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue(historyFormValues(record));
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    const payload = historyPayload(values);
    try {
      if (editing) {
        await api.put(`/api/employee-history/${editing.ID}`, payload);
        message.success('Record updated');
      } else {
        await api.post('/api/employee-history', payload);
        message.success('Record added');
      }
      setModalOpen(false);
      load();
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/employee-history/${id}`);
      message.success('Record deleted');
      load();
    } catch {
      message.error('Delete failed');
    }
  };

  const openAddTarget = () => {
    setEditingTgt(null);
    tgtForm.resetFields();
    setTgtModalOpen(true);
  };

  const openEditTarget = (record) => {
    setEditingTgt(record);
    tgtForm.setFieldsValue({
      team_id:              record.TeamID,
      job_id:               record.JobID ?? undefined,
      hire_date:            dayjs(record.HireDate),
      hire_count:           record.HireCount,
      graduation_target_fte: record.GraduationTargetFTE,
    });
    setTgtModalOpen(true);
  };

  const handleSaveTarget = async () => {
    let values;
    try { values = await tgtForm.validateFields(); } catch { return; }
    setSavingTgt(true);
    const payload = {
      team_id:               values.team_id,
      job_id:                values.job_id ?? null,
      hire_date:             values.hire_date.format('YYYY-MM-DD'),
      hire_count:            values.hire_count,
      graduation_target_fte: values.graduation_target_fte,
    };
    try {
      if (editingTgt) {
        await api.put(`/api/recruitment-targets/${editingTgt.ID}`, payload);
        message.success('Target updated');
      } else {
        await api.post('/api/recruitment-targets', payload);
        message.success('Target added');
      }
      setTgtModalOpen(false);
      loadTargets();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingTgt(false);
    }
  };

  const handleDeleteTarget = async (id) => {
    try {
      await api.delete(`/api/recruitment-targets/${id}`);
      message.success('Target deleted');
      loadTargets();
    } catch {
      message.error('Delete failed');
    }
  };

  const filtered = rows.filter((r) =>
    (!filterEmployee  || r.UserID     === filterEmployee) &&
    (!filterTeam      || r.TeamID     === filterTeam) &&
    (!filterJob       || r.JobID      === filterJob) &&
    (!filterStatus    || r.Status     === filterStatus) &&
    (!filterStartDate || r.StartDate?.startsWith(filterStartDate)) &&
    (!filterEndDate   || r.EndDate?.startsWith(filterEndDate)) &&
    (!filterGradDate  || r.GraduationDate?.startsWith(filterGradDate))
  );

  const strSort  = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
  const dateSort = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');

  const columns = [
    { title: 'Employee',   dataIndex: 'DisplayName',    key: 'DisplayName',    ellipsis: true, sorter: strSort('DisplayName'),    defaultSortOrder: 'ascend' },
    { title: 'Team',       dataIndex: 'TeamName',       key: 'TeamName',       ellipsis: true, sorter: strSort('TeamName') },
    { title: 'Job',        dataIndex: 'JobName',        key: 'JobName',        ellipsis: true, sorter: strSort('JobName'),        render: (v) => v || '—' },
    { title: 'Status',     dataIndex: 'Status',         key: 'Status',         width: 180,     sorter: strSort('Status'),
      render: (v, r) => (
        <Space size={4} wrap>
          <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>
          {r.TransferredIn ? (
            <Tooltip title="Transferred into this team/job from another assignment">
              <Tag color="geekblue" style={{ fontSize: 11 }}>Transfer In</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ) },
    { title: 'Start Date', dataIndex: 'StartDate',      key: 'StartDate',      width: 110,     sorter: dateSort('StartDate') },
    { title: 'Graduation', dataIndex: 'GraduationDate', key: 'GraduationDate', width: 110,     sorter: dateSort('GraduationDate'), render: (v) => v || '—' },
    { title: 'End Date',   dataIndex: 'EndDate',        key: 'EndDate',        width: 110,     sorter: dateSort('EndDate'),
      render: (v) => v || <Typography.Text type="secondary">Current</Typography.Text> },
    { title: 'Training Non-Billable', dataIndex: 'TrainingNonBillable', key: 'TrainingNonBillable', width: 150, align: 'center',
      render: (v) => v ? <Tag color="orange">Yes</Tag> : null },
    { title: 'Referral', dataIndex: 'Referral', key: 'Referral', width: 90, align: 'center',
      render: (v) => v ? <Tag color="purple">Yes</Tag> : null },
    { title: 'Sched Hrs', dataIndex: 'ScheduledHours', key: 'ScheduledHours', width: 95, align: 'right',
      render: (v) => v ?? 40 },
    { title: 'Ignore Recruit.', dataIndex: 'IgnoreInRecruitment', key: 'IgnoreInRecruitment', width: 120, align: 'center',
      render: (v) => v ? <Tag color="red">Yes</Tag> : null },
    ...(canEdit ? [{
      title: '', key: 'actions', width: 90,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this record?" onConfirm={() => handleDelete(r.ID)} okText="Delete" okType="danger">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
    { title: 'Notes', dataIndex: 'Notes', key: 'Notes', ellipsis: true, render: (v) => v || '—' },
  ];

  const tgtColumns = [
    { title: 'Team',      dataIndex: 'TeamName',           key: 'TeamName',           ellipsis: true, sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
    { title: 'Job',       dataIndex: 'JobName',            key: 'JobName',            ellipsis: true, sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? ''), render: (v) => v || '—' },
    { title: 'Hire Date', dataIndex: 'HireDate',           key: 'HireDate',           width: 120,     sorter: (a, b) => (a.HireDate ?? '').localeCompare(b.HireDate ?? '') },
    { title: 'Order Count FTE',      dataIndex: 'HireCount',           key: 'HireCount',           width: 135, sorter: (a, b) => a.HireCount - b.HireCount },
    { title: 'Graduation Target FTE', dataIndex: 'GraduationTargetFTE', key: 'GraduationTargetFTE', width: 170, sorter: (a, b) => a.GraduationTargetFTE - b.GraduationTargetFTE },
    ...(canEdit ? [{
      title: '', key: 'actions', width: 90,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditTarget(r)} />
          <Popconfirm title="Delete this target?" onConfirm={() => handleDeleteTarget(r.ID)} okText="Delete" okType="danger">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {view === 'history' ? 'Employee Assignment History' : 'Recruitment Targets'}
        </Title>
        <Space>
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: 'Employee History', value: 'history' },
              { label: 'Recruitment Targets', value: 'targets' },
            ]}
          />
          {view === 'history' && canEdit && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Record</Button>
          )}
          {view === 'targets' && canEdit && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddTarget}>Add Target</Button>
          )}
        </Space>
      </div>

      {view === 'history' && (
        <>
          <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
                placeholder="Filter by employee"
                options={users.map((u) => ({ value: u.ID, label: u.DisplayName }))}
                onChange={(v) => setFilterEmployee(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
                placeholder="Filter by team"
                options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
                onChange={(v) => setFilterTeam(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
                placeholder="Filter by job"
                options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))}
                onChange={(v) => setFilterJob(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                allowClear style={{ width: '100%' }}
                placeholder="Filter by status"
                options={STATUSES.map((s) => ({ value: s, label: s }))}
                onChange={(v) => setFilterStatus(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch allowClear style={{ width: '100%' }}
                placeholder="Filter by start month"
                optionFilterProp="label"
                options={[...new Set(rows.map((r) => r.StartDate?.slice(0, 7)).filter(Boolean))].sort((a, b) => b.localeCompare(a)).map((m) => ({
                  value: m,
                  label: dayjs(m + '-01').format('MMM YYYY'),
                }))}
                onChange={(v) => setFilterStartDate(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch allowClear style={{ width: '100%' }}
                placeholder="Filter by end month"
                optionFilterProp="label"
                options={[...new Set(rows.map((r) => r.EndDate?.slice(0, 7)).filter(Boolean))].sort((a, b) => b.localeCompare(a)).map((m) => ({
                  value: m,
                  label: dayjs(m + '-01').format('MMM YYYY'),
                }))}
                onChange={(v) => setFilterEndDate(v ?? null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                showSearch allowClear style={{ width: '100%' }}
                placeholder="Filter by graduated month"
                optionFilterProp="label"
                options={[...new Set(rows.map((r) => r.GraduationDate?.slice(0, 7)).filter(Boolean))].sort((a, b) => b.localeCompare(a)).map((m) => ({
                  value: m,
                  label: dayjs(m + '-01').format('MMM YYYY'),
                }))}
                onChange={(v) => setFilterGradDate(v ?? null)}
              />
            </Col>
          </Row>

          <div style={{ marginBottom: 8, marginTop: 4 }}>
            <Popover
              title="Status Definitions"
              trigger="hover"
              content={
                <div style={{ fontSize: 13, lineHeight: 1.8, maxWidth: 420 }}>
                  <div><strong>Accepted Offer</strong> — Candidate accepted offer letter</div>
                  <div><strong>Rescinded Offer</strong> — Before orientation day, candidate is no longer employed</div>
                  <div><strong>No Show</strong> — Did not show up for orientation day</div>
                  <div><strong>Orientation Fallout</strong> — Did not make it past orientation day</div>
                  <div><strong>In Training</strong> — Currently in training</div>
                  <div><strong>Former Trainee</strong> — Termed before graduation</div>
                  <div><strong>In Production</strong> — Active employee post graduation</div>
                  <div><strong>Former Employee</strong> — Former employee</div>
                  <div><strong>Inter Transfer</strong> — Employee no longer in this job/team; moved to new job/team</div>
                  <div style={{ marginTop: 8, color: '#8c8c8c' }}>* <strong>Transfer In</strong> — Flag indicating the new job/team the employee transferred into</div>
                </div>
              }
            >
              <Typography.Text type="secondary" style={{ cursor: 'default', fontSize: 13 }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />Status Explanation Guide
              </Typography.Text>
            </Popover>
          </div>

          <Table
            rowKey="ID"
            dataSource={filtered}
            columns={columns}
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ defaultPageSize: 150, showSizeChanger: true, pageSizeOptions: [50, 100, 150, 250], showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
            locale={{ emptyText: 'No assignment history found.' }}
          />
        </>
      )}

      {view === 'targets' && (
        <Table
          rowKey="ID"
          dataSource={targets}
          columns={tgtColumns}
          loading={loadingTgts}
          size="small"
          pagination={{ defaultPageSize: 150, showSizeChanger: true, pageSizeOptions: [50, 100, 150, 250], showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
          locale={{ emptyText: 'No recruitment targets found.' }}
        />
      )}

      {/* Employee History modal */}
      <Modal maskClosable={false}
        title={editing ? 'Edit Assignment' : 'Add Assignment'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={saving ? 'Saving...' : 'Save'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <EmployeeHistoryFields users={users} teams={teams} jobs={jobs} lockEmployee={!!editing} />
        </Form>
      </Modal>

      {/* Recruitment Targets modal */}
      <Modal maskClosable={false}
        title={editingTgt ? 'Edit Recruitment Target' : 'Add Recruitment Target'}
        open={tgtModalOpen}
        onOk={handleSaveTarget}
        onCancel={() => setTgtModalOpen(false)}
        okText={savingTgt ? 'Saving...' : 'Save'}
        confirmLoading={savingTgt}
        destroyOnClose
      >
        <Form form={tgtForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="team_id" label="Team" rules={[{ required: true, message: 'Team is required' }]}>
            <Select
              showSearch optionFilterProp="label"
              options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
              placeholder="Select team"
            />
          </Form.Item>
          <Form.Item name="job_id" label="Job" rules={[{ required: true, message: 'Job is required' }]}>
            <Select
              showSearch optionFilterProp="label"
              options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))}
              placeholder="Select job"
            />
          </Form.Item>
          <Form.Item name="hire_date" label="Hire Date" rules={[{ required: true, message: 'Hire date is required' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="hire_count" label="Order Count FTE" rules={[{ required: true, message: 'Order count is required' }]}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="e.g. 10.5" />
          </Form.Item>
          <Form.Item
            name="graduation_target_fte"
            label="Graduation Target FTE"
            rules={[
              { required: true, message: 'Graduation Target FTE is required' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value === null || value === undefined) return Promise.resolve();
                  const hireCount = getFieldValue('hire_count');
                  if (hireCount !== null && hireCount !== undefined && value > hireCount) {
                    return Promise.reject(new Error('Graduation Target FTE cannot exceed Order Count FTE'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="e.g. 8.0" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
